import React, { Component } from 'react';
import PropTypes from 'prop-types';
// graphql
import gql from 'graphql-tag';
import { formatRoute } from 'react-router-named-routes';
// adopt
import { adopt } from 'react-adopt';
// global variables
import { userKinds } from 'config';
// our components
import { USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import { Mutation } from 'components/common/Query';
import UserCreate from './Create';

/**
 * Mutation to add users
 */
export const ADD_USER_MUTATION = gql`
  mutation(
    $fullname: String!
    $password1: String!
    $password2: String!
    $rank: UserRank!
    $cardProvider: AdminCreateUserFormCardProvider
    $email: String
    $phone: String
    $isActive: Boolean!
    $changePassOnLogin: Boolean!
    $allowedIps: [String]
    $negativeCredit: Boolean
  ) {
    createAdmin(
      input: {
        fullname: $fullname
        password1: $password1
        password2: $password2
        rank: $rank
        cardProvider: $cardProvider
        email: $email
        phone: $phone
        isActive: $isActive
        changePassOnLogin: $changePassOnLogin
        allowedIps: $allowedIps
        negativeCredit: $negativeCredit
      }
    ) {
      user {
        id
        email
        phone
        fullname
      }
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
`;

export const ADD_AGENCY_MUTATION = gql`
  mutation(
    $fullname: String!
    $password1: String!
    $password2: String!
    $rank: UserRank!
    $cardProvider: AdminCreateAgencyFormCardProvider
    $email: String
    $phone: String
    $isActive: Boolean!
    $changePassOnLogin: Boolean!
    $commissionLead: Float!
    $commissionLeadRevolico: Float!
    $allowedIps: [String]
    $negativeCredit: Boolean
    $paymentMethod: AdminCreateAgencyFormPaymentMethod!
  ) {
    createAgency(
      input: {
        fullname: $fullname
        password1: $password1
        password2: $password2
        rank: $rank
        cardProvider: $cardProvider
        email: $email
        phone: $phone
        isActive: $isActive
        changePassOnLogin: $changePassOnLogin
        commissionLead: $commissionLead
        commissionLeadRevolico: $commissionLeadRevolico
        allowedIps: $allowedIps
        negativeCredit: $negativeCredit
        paymentMethod: $paymentMethod
      }
    ) {
      agency {
        id
        email
        phone
        fullname
        commissionLead
        commissionLeadRevolico
      }
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
`;

/**
 * Mutation to add user agency
 */
export const ADD_USER_AGENCY_MUTATION = gql`
  mutation(
    $fullname: String!
    $email: String
    $phone: String
    $leadAgency: ID!
    $lead: AdminCreateUserAgencyFormLead!
    $allowedIps: [String]
    $negativeCredit: Boolean
    $cardProvider: AdminCreateUserAgencyFormCardProvider
  ) {
    createUserAgency(
      input: {
        fullname: $fullname
        email: $email
        phone: $phone
        leadAgency: $leadAgency
        lead: $lead
        allowedIps: $allowedIps
        negativeCredit: $negativeCredit
        cardProvider: $cardProvider
      }
    ) {
      userAgency {
        id
        email
        phone
        fullname
        leadAgency {
          id
          fullname
        }
        lead
      }
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
`;

/**
 * @class
 * @extends React.Component
 */
class UserCreateContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      errors: [],
      isStaff: false,
    };

    this.Compose = adopt({
      mutateUserCreate: this.createUser,
      mutateAgencyCreate: this.createAgency,
      mutateUserAgencyCreate: this.createUserAgency,
    });
  }

  /**
   * @description
   * Mutation create callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  createUser = ({ render }) => (
    <Mutation
      mutation={ADD_USER_MUTATION}
      onCompleted={this.userCreateCompleted('createAdmin', 'user')}
    >
      {(handleUserCreate, { loading }) =>
        render({ handleUserCreate, userLoading: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation create callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  createAgency = ({ render }) => (
    <Mutation
      mutation={ADD_AGENCY_MUTATION}
      onCompleted={this.userCreateCompleted('createAgency', 'agency')}
    >
      {(handleAgencyCreate, { loading }) =>
        render({ handleAgencyCreate, agencyLoading: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation create callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  createUserAgency = ({ render }) => (
    <Mutation
      mutation={ADD_USER_AGENCY_MUTATION}
      onCompleted={this.userCreateCompleted('createUserAgency', 'userAgency')}
    >
      {(handleUserAgencyCreate, { loading }) =>
        render({ handleUserAgencyCreate, userAgencyLoading: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation callback when add user is completed.
   *
   * @param {Object} action - Mutation name.
   * @param {Object} entity - Type name.
   *
   * @returns {Function} Callback function to execute in complete.
   */
  userCreateCompleted = (action, entity) => async data => {
    const history = this.context;
    const { isStaff } = this.state;
    const { errors } = data[action];
    if (errors) {
      this.setState({ errors });
    } else if (isStaff) {
      history.push(
        formatRoute(USER.PERMISSION, { id: data[action][entity].id }),
      );
    } else {
      history.push(
        formatRoute(`${USER.DETAILS}#logs`, { id: data[action][entity].id }),
      );
    }
  };

  /**
   * @description
   * Render UserCreate component.
   */
  render() {
    const { errors } = this.state;
    const { agencyId, fromAgency } = this.props;
    return (
      <this.Compose>
        {({
          mutateUserCreate: { handleUserCreate, userLoading },
          mutateAgencyCreate: { handleAgencyCreate, agencyLoading },
          mutateUserAgencyCreate: { handleUserAgencyCreate, userAgencyLoading },
        }) => (
          <UserCreate
            userCreate={({ userKind, ...data }) => {
              this.setState(
                {
                  isStaff: data.rank === 'STAFF',
                },
                () => {
                  switch (userKind) {
                    case userKinds.AGENCY: {
                      handleAgencyCreate({ variables: data });
                      break;
                    }
                    case userKinds.USER_AGENCY: {
                      handleUserAgencyCreate({ variables: data });
                      break;
                    }
                    default: {
                      handleUserCreate({ variables: data });
                    }
                  }
                },
              );
            }}
            loading={userLoading || agencyLoading || userAgencyLoading}
            errors={errors}
            fromAgency={fromAgency}
            agencyId={agencyId}
          />
        )}
      </this.Compose>
    );
  }
}

UserCreateContainer.propTypes = {
  agencyId: PropTypes.string,
  fromAgency: PropTypes.bool,
};

UserCreateContainer.defaultProps = {
  agencyId: '',
  fromAgency: false,
};

export default UserCreateContainer;
